import { access, mkdir, readFile, unlink, writeFile } from "node:fs/promises";
import { join } from "node:path";
import { cwd } from "node:process";
import { cancel, intro, outro, spinner } from "@clack/prompts";
import kleur from "kleur";

const { green, yellow, cyan, bold } = kleur;

const HOOK_SCRIPT = `#!/bin/bash
# Bumper auto-update hook
# This hook runs after each commit to update .bumper/context.md

# Skip if SKIP_BUMPER_HOOKS is set
if [ -n "$SKIP_BUMPER_HOOKS" ]; then
  exit 0
fi

# Check if we're in a Bumper project
if [ ! -d ".bumper" ]; then
  exit 0
fi

# Detect if hook is already running (prevent recursion)
if [ -n "$BUMPER_HOOK_RUNNING" ]; then
  exit 0
fi

# Set environment variable to prevent recursion
export BUMPER_HOOK_RUNNING=1

# Update lastVerified timestamp in context.md
CONTEXT_FILE=".bumper/context.md"
if [ -f "$CONTEXT_FILE" ]; then
  TODAY=$(date +%Y-%m-%d)

  # Update lastVerified field with today's date
  if grep -q "lastVerified:" "$CONTEXT_FILE"; then
    sed -i "s/lastVerified: .*/lastVerified: $TODAY/" "$CONTEXT_FILE"
  fi

  # Stage and commit if context.md changed
  if ! git diff --cached --quiet "$CONTEXT_FILE" 2>/dev/null; then
    git add "$CONTEXT_FILE" 2>/dev/null
    git commit --amend --no-edit --no-verify 2>/dev/null || true
  fi
fi

unset BUMPER_HOOK_RUNNING
exit 0
`;

async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}

async function getGitDir(): Promise<string | null> {
  try {
    const gitDir = join(cwd(), ".git");
    if (await fileExists(gitDir)) {
      return gitDir;
    }
    return null;
  } catch {
    return null;
  }
}

async function isGitRepo(): Promise<boolean> {
  return (await getGitDir()) !== null;
}

export async function hooksInstallCommand() {
  intro(cyan(bold("🪝 Install Bumper Git Hooks")));

  const s = spinner();

  try {
    s.start("Checking git repository");

    if (!(await isGitRepo())) {
      cancel("❌ Not a git repository. Initialize git first with: git init");
      process.exit(1);
    }

    const gitDir = await getGitDir();
    if (!gitDir) {
      cancel("❌ Could not find .git directory");
      process.exit(1);
    }

    const hooksDir = join(gitDir, "hooks");
    const postCommitHook = join(hooksDir, "post-commit");

    // Create hooks directory if it doesn't exist
    s.start("Creating hooks directory");
    await mkdir(hooksDir, { recursive: true });

    // Check if hook already exists
    if (await fileExists(postCommitHook)) {
      const existingHook = await readFile(postCommitHook, "utf-8");
      if (existingHook.includes("Bumper auto-update hook")) {
        s.stop();
        outro(yellow("! Bumper hooks already installed"));
        return;
      }
    }

    // Write the hook script
    s.start("Installing post-commit hook");
    await writeFile(postCommitHook, HOOK_SCRIPT, "utf-8");

    // Make it executable (Unix-like systems)
    try {
      await new Promise<void>((resolve, reject) => {
        const fs = require("node:fs");
        fs.chmod(postCommitHook, 0o755, (err: Error | null) => {
          if (err) reject(err);
          else resolve();
        });
      });
    } catch {
      // Chmod might fail on Windows, but that's okay
    }

    s.stop();

    outro(
      [
        green("✓ Bumper git hooks installed successfully!"),
        "",
        "What's installed:",
        "  " +
          cyan("post-commit hook") +
          " - Auto-updates context.md timestamps",
        "",
        "To disable hooks for a specific commit:",
        `  ${yellow("SKIP_BUMPER_HOOKS=1 git commit ...")}`,
        "",
        "To uninstall hooks:",
        `  ${yellow("bmpr hooks uninstall")}`,
        "",
        "Happy committing! 🎉",
      ].join("\n"),
    );
  } catch (error) {
    s.stop();
    cancel("Failed to install hooks");
    if (error instanceof Error) {
      console.error("Error:", error.message);
    }
    process.exit(1);
  }
}

export async function hooksUninstallCommand() {
  intro(cyan(bold("🪝 Uninstall Bumper Git Hooks")));

  const s = spinner();

  try {
    s.start("Checking git repository");

    const gitDir = await getGitDir();
    if (!gitDir) {
      cancel("❌ Not a git repository");
      process.exit(1);
    }

    const postCommitHook = join(gitDir, "hooks", "post-commit");

    if (!(await fileExists(postCommitHook))) {
      s.stop();
      outro(yellow("! No Bumper hooks found to uninstall"));
      return;
    }

    // Check if it's a Bumper hook before deleting
    const hookContent = await readFile(postCommitHook, "utf-8");
    if (!hookContent.includes("Bumper auto-update hook")) {
      cancel(
        "❌ This doesn't appear to be a Bumper hook. Not removing to be safe.",
      );
      process.exit(1);
    }

    s.start("Removing post-commit hook");
    await unlink(postCommitHook);

    s.stop();

    outro(
      [
        green("✓ Bumper git hooks uninstalled"),
        "",
        "You can reinstall anytime with:",
        `  ${yellow("bmpr hooks install")}`,
      ].join("\n"),
    );
  } catch (error) {
    s.stop();
    cancel("Failed to uninstall hooks");
    if (error instanceof Error) {
      console.error("Error:", error.message);
    }
    process.exit(1);
  }
}

export async function hooksCommand() {
  intro(cyan(bold("🪝 Bumper Git Hooks")));

  console.log(`
${bold("Usage:")}
  bmpr hooks <subcommand>

${bold("Subcommands:")}
  ${yellow("install")}   Install Bumper git hooks
  ${yellow("uninstall")} Uninstall Bumper git hooks

${bold("Examples:")}
  bmpr hooks install
  bmpr hooks uninstall
  `);
}
